import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Grid,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  TextareaAutosize,
  Button,
  Tooltip,
} from "@mui/material";
import {
  TableRowData,
  AuditData,
  TotalAmountData,
} from "../../../src/types/table";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";


interface PaginatedTableProps {
  tableData: TableRowData[];
  setTableData: React.Dispatch<React.SetStateAction<TableRowData[]>>;
  auditData: AuditData | undefined;
  totalAmountData: TotalAmountData | undefined;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setCommentsArray: React.Dispatch<React.SetStateAction<number[]>>;
  commentsArray: number[];
  loading?: boolean;
  order: string;
  orderBy: string;
  setOrder: React.Dispatch<React.SetStateAction<string>>;
  setOrderBy: React.Dispatch<React.SetStateAction<string>>;
  commentsDisabled?: boolean;
  filtersChanged?: boolean;
  cbtTypeOptions?: any[];
  companyCodeOptions?: any[];
  L3CodeOptions?: any[];
  cbtFilter?: string;
  setCbtFilter?: React.Dispatch<React.SetStateAction<string>>;
  companyFilter?: string;
  setCompanyFilter?: React.Dispatch<React.SetStateAction<string>>;
  glFilter?: string;
  setGlFilter?: React.Dispatch<React.SetStateAction<string>>;
  isFilterChanged?: boolean;
  reportingGroupOptions?: any[];
  rgFilter?: string;
  setRgFilter?: React.Dispatch<React.SetStateAction<string>>;
  handleTableFilterChange?: (
    value: string,
    key: "cbtFilter" | "companyFilter" | "glFilter" | "rgFilter",
  ) => void;
  filtersVisible?: boolean;
}

/**
 * PaginatedTable component renders a table with sorting, pagination, and comment editing features.
 * @param {PaginatedTableProps} props - The props for the table, including data, sorting, pagination, and callbacks.
 */
const PaginatedTable: React.FC<PaginatedTableProps> = ({
  tableData,
  setTableData,
  auditData,
  totalAmountData,
  setPage,
  page,
  commentsArray,
  setCommentsArray,
  loading = false,
  order,
  orderBy,
  setOrder,
  setOrderBy,
  commentsDisabled = false,
  filtersChanged = false,
  cbtTypeOptions = [{ CBTType: "" }],
  companyCodeOptions = [{ CompanyCode: "" }],
  L3CodeOptions = [{ L3Code: "" }],
  cbtFilter,
  setCbtFilter,
  companyFilter,
  setCompanyFilter,
  reportingGroupOptions = [{ L3Description: "" }],
  rgFilter,
  setRgFilter,
  glFilter,
  setGlFilter,
  isFilterChanged,
  handleTableFilterChange,
  filtersVisible = true,
}) => {
  const [sortState, setSortState] = useState({
    order: "asc",
    orderBy: "Company_Code",
  });

  const cbtOptions = useMemo(() => {
    const set = new Set<string>();
    if (Array.isArray(cbtTypeOptions)) {
      cbtTypeOptions.forEach((it: any) => {
        const code = it?.CBTType ?? it?.CBTType;
        if (code) set.add(String(code));
      });
    }
    return Array.from(set).filter((v) => v !== "");
  }, [tableData, cbtTypeOptions]);

  const companyOptions = useMemo(() => {
    const set = new Set<string>();
    if (Array.isArray(companyCodeOptions)) {
      companyCodeOptions.forEach((it: any) => {
        const code = it?.CompanyCode ?? it?.CompanyCode;
        if (code) set.add(String(code));
      });
    }
    return Array.from(set).filter((v) => v !== "");
  }, [tableData, companyCodeOptions]);

  const reportingOptions = useMemo(() => {
    const set = new Set<string>();
    if (Array.isArray(reportingGroupOptions)) {
      reportingGroupOptions.forEach((it: any) => {
        const description = it?.L3Description ?? it?.L3Description;
        if (description) set.add(String(description));
      });
    }
    return Array.from(set).filter((v) => v !== "");
  }, [tableData, reportingGroupOptions]);


  const glOptions = useMemo(() => {
    const set = new Set<string>();

    if (Array.isArray(L3CodeOptions)) {
      L3CodeOptions.forEach((it: any) => {
        const code = it?.L3Code ?? it?.L3Code;
        if (code) set.add(String(code));
      });
    }

    const result = Array.from(set).filter((v) => v !== "");

    return result;
  }, [tableData, L3CodeOptions]);

  /**
   * Handles page change for the table pagination.
   * @param _event - The event object (unused).
   * @param newPage - The new page number to set.
   */
  const handlePageChange = (
    _event: any,
    newPage: React.SetStateAction<number>,
  ) => {
    setPage(newPage);
  };

  /**
   * Handles changes to the comment text area for a specific row.
   * Updates the table data and tracks which comments have changed.
   * @param CommentsID - The ID of the comment being changed.
   * @param CompanyId - The ID of the company for the row.
   * @param newComment - The new comment text.
   */
  const handleCommentChange = (
    CommentsID: number,
    CompanyId: number,
    newComment: string,
  ) => {
    setTableData((prev) => {
      const updatedData = prev.map((row: TableRowData) => {
        if (row.CommentsID === CommentsID && row.CompanyID === CompanyId) {
          return { ...row, Comments: newComment };
        }
        return row;
      });
      return updatedData;
    });

    if (!commentsArray.includes(CommentsID)) {
      setCommentsArray((prev) => [...prev, CommentsID]);
    }
  };

  /**
   * Handles sorting when a column header is clicked.
   * Updates the sort state and triggers parent sort state updates.
   * @param property - The column property to sort by.
   */
  const handleRequestSort = (property: string) => {
    let newOrder = "asc";
    if (property !== sortState.orderBy || sortState.order === "asc") {
      newOrder = "desc";
    }
    setSortState({ order: newOrder, orderBy: property });
    setOrder(newOrder);
    setOrderBy(property);
    setImmediateSort({ order: newOrder, orderBy: property }); // new state for immediate feedback
  };

  const [immediateSort, setImmediateSort] = useState(sortState);

  /**
   * Updates the immediateSort state whenever sortState changes.
   * Ensures the sort arrow updates immediately for user feedback.
   */
  useEffect(() => {
    setImmediateSort(sortState);
  }, [sortState]);

  // ─────────────────────────────────────────────────────────────────────────

  // ── Floating horizontal scrollbar ────────────────────────────────────────
  // tableContainerRef  → the MUI TableContainer (the real scrollable div)
  // stickyScrollRef    → the fixed floating scrollbar bar
  // innerWidthRef      → phantom wide div inside the bar (sets thumb size)
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const innerWidthRef = useRef<HTMLDivElement>(null);
  const [floatingBarVisible, setFloatingBarVisible] = useState(false);
  const [floatingBarStyle, setFloatingBarStyle] = useState<React.CSSProperties>({});
  const [showPreviousComments, setShowPreviousComments] = useState(false);

  useEffect(() => {
    const tableEl = tableContainerRef.current;
    const stickyEl = stickyScrollRef.current;
    const innerEl = innerWidthRef.current;
    if (!tableEl || !stickyEl || !innerEl) return;

    // ── Sync scroll width ──────────────────────────────────────────────────
    const syncWidth = () => {
      innerEl.style.width = `${tableEl.scrollWidth}px`;
    };
    syncWidth();

    // ── Bidirectional scroll sync ──────────────────────────────────────────
    let isSyncingFromTable = false;
    let isSyncingFromSticky = false;

    const onTableScroll = () => {
      if (isSyncingFromSticky) return;
      isSyncingFromTable = true;
      stickyEl.scrollLeft = tableEl.scrollLeft;
      isSyncingFromTable = false;
    };

    const onStickyScroll = () => {
      if (isSyncingFromTable) return;
      isSyncingFromSticky = true;
      tableEl.scrollLeft = stickyEl.scrollLeft;
      isSyncingFromSticky = false;
    };

    tableEl.addEventListener("scroll", onTableScroll);
    stickyEl.addEventListener("scroll", onStickyScroll);

    // ── Keep bar width/position in sync with the table's bounding rect ─────
    const updateBarPosition = () => {
      const rect = tableEl.getBoundingClientRect();
      setFloatingBarStyle({
        left: rect.left,
        width: rect.width,
      });
    };
    updateBarPosition();
    window.addEventListener("resize", updateBarPosition);
    window.addEventListener("scroll", updateBarPosition, true);

    // ── IntersectionObserver — show bar only while table is visible ────────
    const io = new IntersectionObserver(
      ([entry]) => {
        setFloatingBarVisible(entry.isIntersecting);
        if (entry.isIntersecting) updateBarPosition();
      },
      { threshold: 0 },
    );
    io.observe(tableEl);

    // ── ResizeObserver — re-sync phantom width on content reflow ──────────
    const ro = new ResizeObserver(() => {
      syncWidth();
      updateBarPosition();
    });
    ro.observe(tableEl);

    return () => {
      tableEl.removeEventListener("scroll", onTableScroll);
      stickyEl.removeEventListener("scroll", onStickyScroll);
      window.removeEventListener("resize", updateBarPosition);
      window.removeEventListener("scroll", updateBarPosition, true);
      io.disconnect();
      ro.disconnect();
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────
  const totalColumns = showPreviousComments ? 11 : 10;
  const rowsPerPage = 100;
  const from = auditData?.TotalRecords ? page * rowsPerPage + 1 : 0;
  const to = Math.min((page + 1) * rowsPerPage, auditData?.TotalRecords ?? 0);
  return (
    <>
      {/* Outer wrapper — sticky bottom: 0 detaches naturally when this Box ends,
            so the scrollbar never overlaps the footer below */}
      <Box sx={{ position: "relative", mb: 5, mt: 0.75 }}>
        {/* <Paper elevation={3} sx={{ p: 0.75 }}> */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {/*
          * overflowX:"clip" on this wrapper visually clips the native
          * horizontal scrollbar out of view without affecting:
          *   - vertical scrolling (overflowY is untouched)
          *   - programmatic scrollLeft used by the sticky sync
          * The sticky strip below is the only visible H-scrollbar.
          */}
          <Box sx={{ overflowX: "clip" }}>
            <TableContainer sx={{ maxHeight: filtersVisible ? 'calc(100vh - 300px)' : 'calc(100vh - 170px)', overflow: 'auto' }}>
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                }}
              >
                <Tooltip
                  title={
                    showPreviousComments
                      ? "Collapse Previous Comments"
                      : "Expand Previous Comments"
                  }
                  placement="left"
                  arrow
                >
                  <Box
                    onClick={() => setShowPreviousComments(prev => !prev)}
                    sx={{
                      cursor: "pointer",
                      width: 22,
                      height: 40,
                      backgroundColor: "#0094f2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "12px 0 0 12px", // rounded like screenshot
                      boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#007acc",
                      },
                    }}
                  >
                    {showPreviousComments ? (
                      <KeyboardArrowRightIcon sx={{ color: "#fff", fontSize: 18 }} />
                    ) : (
                      <KeyboardArrowLeftIcon sx={{ color: "#fff", fontSize: 18 }} />
                    )}
                  </Box>
                  </Tooltip>
              </Box>
               
              <Table
                size="small"
                sx={{
                  borderCollapse: "collapse",
                  minWidth: showPreviousComments ? 1200 : 1000,
                  "& thead tr, & tbody tr": {
                    height: "24px", // Row height
                  },
                  "& th, & td": {
                    padding: "2px 4px", // Cell padding
                    border: "none", // Optional: borders
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    className="sticky-header"
                    sx={{
                      height: "24px", // Adjust row height
                      "& td, & th": {
                        padding: "2px 2px", // Reduce cell padding
                      },
                      bgcolor: "#0094f2",
                      color: "white",
                    }}
                  >
                    <TableCell
                      sx={{ textAlign: "center", width: 80, minWidth: 80, verticalAlign: "top" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          alignItems: "center",
                        }}
                      >
                        <TableSortLabel
                          sx={{ whiteSpace: "nowrap", flexDirection: "row" }}
                          active={true}
                          direction={
                            immediateSort.orderBy === "CBT_TYPE"
                              ? (immediateSort.order as "desc" | "asc")
                              : "asc"
                          }
                          onClick={() => handleRequestSort("CBT_TYPE")}
                        >
                          PL/BS
                        </TableSortLabel>
                        <FormControl size="small" sx={{ minWidth: 70 }}>
                          <Select
                            value={cbtFilter ?? "All"}
                            onChange={(e) => {
                              const v = String(e.target.value);
                              setCbtFilter?.(v);
                              handleTableFilterChange?.(v, "cbtFilter");
                            }}
                            displayEmpty
                            disabled={
                              filtersChanged ||
                              (tableData.length > 0 ? false : true)
                            }
                            MenuProps={{
                              PaperProps: {
                                elevation: 0,
                                sx: { boxShadow: "none", border: "none" },
                              },
                            }}
                            sx={{
                              color: "white",
                              fontSize: "12px",
                              border: "1px solid rgba(255,255,255,0.9)",
                              borderRadius: "8px",
                              padding: "0 6px",
                              height: 24,
                              ".MuiSelect-select": {
                                paddingTop: "2px",
                                paddingBottom: "2px",
                                fontSize: "12px",
                              },
                              ".MuiSvgIcon-root": { color: "white" },
                              "&.Mui-disabled .MuiSvgIcon-root": {
                                color: "black !important",
                              },
                              // make border and text black when disabled
                              "&.Mui-disabled": {
                                borderColor: "rgba(0,0,0,0.9)",
                                color: "black",
                                ".MuiSelect-select": { color: "black" },
                                ".MuiInputBase-input": { color: "black" },
                                ".MuiSvgIcon-root": { color: "black !important" },
                              },
                              "&:hover, &.Mui-focused, &.Mui-focusVisible, & .MuiSelect-select:focus":
                              {
                                boxShadow: "none !important",
                                outline: "none !important",
                                borderColor: "rgba(255,255,255,0.9) !important",
                                WebkitBoxShadow: "none !important",
                              },
                              // also clear any outlined-input fieldset if present
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "& .MuiInputBase-input": {
                                color: "white",
                                fontSize: "12px",
                              },
                            }}
                            inputProps={{ "aria-label": "cbt-filter" }}
                          >
                            <MenuItem value="All">All</MenuItem>
                            {cbtOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: "left", width: 130, minWidth: 130, verticalAlign: "top" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          alignItems: "flex-start",
                        }}
                      >
                        <TableSortLabel
                          sx={{ whiteSpace: "nowrap", flexDirection: "row" }}
                          active={true}
                          direction={
                            immediateSort.orderBy === "Company_Code"
                              ? (immediateSort.order as "desc" | "asc")
                              : "asc"
                          }
                          onClick={() => handleRequestSort("Company_Code")}
                        >
                          Company
                        </TableSortLabel>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <Autocomplete
                            size="small"
                            options={["All", ...companyOptions]}
                            value={companyFilter ?? "All"}
                            onChange={(_event: any, newValue: any) => {
                              const v = String(newValue ?? "All");
                              setCompanyFilter?.(v);
                              handleTableFilterChange?.(v, "companyFilter");
                            }}
                            renderInput={(params: any) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Search company"
                                sx={{
                                  color: "white",
                                  fontSize: "12px",
                                  fontFamily: "inherit",
                                  border: "1px solid rgba(255,255,255,0.9)",
                                  borderRadius: "8px",
                                  "& .MuiOutlinedInput-root": {
                                    padding: "0 6px",
                                    height: 24,
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    backgroundColor: "rgba(0,0,0,0.1)",
                                    borderRadius: "8px",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: "white",
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    padding: "2px 4px",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "& .MuiSvgIcon-root": { color: "white" },
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  "aria-label": "company-filter",
                                }}
                              />
                            )}
                            openOnFocus
                            // disableClearable
                            disabled={
                              filtersChanged ||
                              (tableData.length > 0 ? false : true)
                            }
                            sx={{
                              minWidth: 90,
                              width: 100,
                              fontsize: "12px",
                              fontFamily: "inherit",
                              "& .MuiAutocomplete-inputRoot": {
                                color: "white",
                                fontSize: "12px",
                                fontFamily: "inherit",
                              },
                            }}
                            ListboxProps={{
                              sx: {
                                // target listbox items directly
                                "& li": {
                                  fontSize: "12px",
                                  fontFamily: "inherit",
                                  paddingTop: "4px",
                                  paddingBottom: "4px",
                                },
                                // also ensure the listbox itself uses same font
                                fontSize: "12px",
                                fontFamily: "inherit",
                              },
                            }}
                            renderOption={(props, option: any) => (
                              <li
                                {...props}
                                style={{
                                  fontSize: 12,
                                  fontFamily: "inherit",
                                  paddingTop: 4,
                                  paddingBottom: 4,
                                }}
                              >
                                {option}
                              </li>
                            )}
                            isOptionEqualToValue={(option: any, value: any) =>
                              option === value
                            }
                          />
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "left",
                        width: 150,
                        minWidth: 150,
                        maxWidth: 160,
                        verticalAlign: "top",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          alignItems: "flex-start",
                        }}
                      >
                        <TableSortLabel
                          sx={{ whiteSpace: "nowrap", flexDirection: "row" }}
                          active={true}
                          direction={
                            immediateSort.orderBy === "Reporting_Group"
                              ? (immediateSort.order as "desc" | "asc")
                              : "asc"
                          }
                          onClick={() => handleRequestSort("Reporting_Group")}
                        >
                          Reporting Group
                        </TableSortLabel>
                        <FormControl size="small"
                          sx={{
                            minWidth: 170,
                            width: 170
                          }}>
                          <Autocomplete
                            size="small"
                            options={["All", ...reportingOptions]}
                            value={rgFilter ?? "All"}
                            onChange={(_event: any, newValue: any) => {
                              const v = String(newValue ?? "All");
                              setRgFilter?.(v);
                              handleTableFilterChange?.(v, "rgFilter");
                            }}
                            renderInput={(params: any) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Search reporting group"
                                sx={{
                                  color: "white",
                                  fontSize: "12px",
                                  fontFamily: "inherit",
                                  border: "1px solid rgba(255,255,255,0.9)",
                                  borderRadius: "8px",
                                  "& .MuiOutlinedInput-root": {
                                    padding: "0 6px",
                                    height: 24,
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    backgroundColor: "rgba(0,0,0,0.1)",
                                    borderRadius: "8px",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: "white",
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    padding: "2px 4px",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "& .MuiSvgIcon-root": { color: "white" },
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  "aria-label": "reporting-group-filter",
                                }}
                              />
                            )}
                            openOnFocus
                            //disableClearable
                            disabled={
                              filtersChanged ||
                              (tableData.length > 0 ? false : true)
                            }
                            sx={{
                              minWidth: 170,
                              width: 170,
                              fontsize: "12px",
                              fontFamily: "inherit",
                              "& .MuiAutocomplete-inputRoot": {
                                color: "white",
                                fontSize: "12px",
                                fontFamily: "inherit",
                              },
                            }}
                            ListboxProps={{
                              sx: {
                                // target listbox items directly
                                "& li": {
                                  fontSize: "12px",
                                  fontFamily: "inherit",
                                  paddingTop: "4px",
                                  paddingBottom: "4px",
                                },
                                // also ensure the listbox itself uses same font
                                fontSize: "12px",
                                fontFamily: "inherit",
                              },
                            }}
                            renderOption={(props, option: any) => (
                              <li
                                {...props}
                                style={{
                                  fontSize: 12,
                                  fontFamily: "inherit",
                                  paddingTop: 4,
                                  paddingBottom: 4,
                                }}
                              >
                                {option}
                              </li>
                            )}
                            isOptionEqualToValue={(option: any, value: any) =>
                              option === value
                            }
                          />
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "left",
                        width: 170,
                        maxWidth: 170,
                        verticalAlign: "top",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          alignItems: "flex-start",
                        }}
                      >
                        <TableSortLabel
                          sx={{ whiteSpace: "nowrap", flexDirection: "row" }}
                          active={true}
                          direction={
                            immediateSort.orderBy === "Gl_Account"
                              ? (immediateSort.order as "desc" | "asc")
                              : "asc"
                          }
                          onClick={() => handleRequestSort("Gl_Account")}
                        >
                          GL&nbsp;Account
                        </TableSortLabel>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Autocomplete
                            size="small"
                            options={["All", ...glOptions]}
                            value={glFilter ?? "All"}
                            onChange={(_event: any, newValue: any) => {
                              const v = String(newValue ?? "All");
                              setGlFilter?.(v);
                              handleTableFilterChange?.(v, "glFilter");
                            }}
                            renderInput={(params: any) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Search GL"
                                sx={{
                                  color: "white",
                                  fontSize: "12px",
                                  fontFamily: "inherit",
                                  border: "1px solid rgba(255,255,255,0.9)",
                                  borderRadius: "8px",
                                  "& .MuiOutlinedInput-root": {
                                    padding: "0 6px",
                                    height: 24,
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    backgroundColor: "rgba(0,0,0,0.1)",
                                    borderRadius: "8px",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: "white",
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    padding: "2px 4px",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "& .MuiSvgIcon-root": { color: "white" },
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  "aria-label": "company-filter",
                                }}
                              />
                            )}
                            openOnFocus
                            // disableClearable
                            disabled={
                              filtersChanged ||
                              (tableData.length > 0 ? false : true)
                            }
                            sx={{
                              minWidth: 120,
                              width: 120,
                              fontsize: "12px",
                              fontFamily: "inherit",
                              "& .MuiAutocomplete-inputRoot": {
                                color: "white",
                                fontSize: "12px",
                                fontFamily: "inherit",
                              },
                            }}
                            ListboxProps={{
                              sx: {
                                // target listbox items directly
                                "& li": {
                                  fontSize: "12px",
                                  fontFamily: "inherit",
                                  paddingTop: "4px",
                                  paddingBottom: "4px",
                                },
                                // also ensure the listbox itself uses same font
                                fontSize: "12px",
                                fontFamily: "inherit",
                              },
                            }}
                            renderOption={(props, option: any) => (
                              <li
                                {...props}
                                style={{
                                  fontSize: 12,
                                  fontFamily: "inherit",
                                  paddingTop: 4,
                                  paddingBottom: 4,
                                }}
                              >
                                {option}
                              </li>
                            )}
                            isOptionEqualToValue={(option: any, value: any) =>
                              option === value
                            }
                          />
                        </FormControl>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ textAlign: "right", width: 100, minWidth: 100, verticalAlign: "top" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                        Reporting
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "right", width: 100, minWidth: 100, verticalAlign: "top" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                        Comparison
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: "right", width: 100, minWidth: 100, verticalAlign: "top", whiteSpace: "nowrap", padding: "2px 4px" }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", fontSize: "12px", fontFamily: "inherit", whiteSpace: "nowrap", height: "24px", minHeight: "24px", padding: 0, margin: 0 }}>
                        <TableSortLabel
                          active={true}
                          direction={
                            immediateSort.orderBy === "Variance"
                              ? (immediateSort.order as "desc" | "asc")
                              : "asc"
                          }
                          onClick={() => handleRequestSort("Variance")}
                          sx={{ display: "flex", alignItems: "center", fontSize: "12px", fontFamily: "inherit", whiteSpace: "nowrap", padding: 0, margin: 0 }}
                        >
                          Variance
                        </TableSortLabel>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{ textAlign: "right", width: 100, minWidth: 100, verticalAlign: "top" }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "right", gap: 0.5 }}>
                        Variance %
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        width: 50,
                        minWidth: 50,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        verticalAlign: "top",
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        Risk Criteria
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: "center", minWidth: 220, width: 220, verticalAlign: "top" }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        Comments (max. 4000 characters)
                      </Box>
                    </TableCell>
                    {showPreviousComments && (
                      <TableCell
                        sx={{ textAlign: "center", minWidth: 220, width: 220 }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          Previous Month Comments
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={totalColumns}
                        align="center"
                        sx={{ height: "200px" }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                        >
                          {/* <CircularProgress /> */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : !filtersChanged && tableData.length > 0 ? (
                    tableData.map((row, index) => {
                      const variancePercent = parseFloat(
                        String(row.VariancePercent),
                      );
                      const color = row.varianceColorCode === "1" ? "green" : "red";
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: row.IsRowHighlight
                              ? "#f5d142"
                              : index % 2 === 0
                                ? "#f5f7fa"
                                : "#ffffff",
                          }}
                        >
                          {/* PL/BS Column */}
                          <TableCell
                            sx={{
                              textAlign: "center",
                              width: 80,
                            }}
                          >{`${row.CBTType}`}</TableCell>
                          {/* Company Column */}
                          <TableCell
                            sx={{
                              textAlign: "left",
                              width: 150,
                            }}
                          >{`${row.CompanyCode}: ${row.CompanyName}`}
                          </TableCell>
                          {/* Reporting Group Column */}
                          <TableCell
                            sx={{
                              textAlign: "left",
                              width: 180,
                              minWidth: 180,
                              maxWidth: 180,
                              whiteSpace: "wrap", 
                            }}
                          >{`${row.ReportingGroup || ""}`}
                          </TableCell>
                          {/* GL Account Column */}
                          <TableCell
                            sx={{
                              fontSize: "0.75rem",
                              width: 180,
                              minWidth: 180,
                              maxWidth: 180,
                              whiteSpace: "normal",           
                              wordBreak: "break-word",        
                              lineHeight: 1.2,  
                            }}
                          >{`${row.GLAccountCode} - ${row.GLAccountName}`}</TableCell>
                          {/* Reporting Column */}
                          <TableCell
                            sx={{
                              textAlign: "right",
                              width: 60,
                            }}
                          >
                            {Number(row.ReportingValue).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </TableCell>
                          {/* Comparison Column */}
                          <TableCell
                            sx={{
                              textAlign: "right",
                              width: 60,
                            }}
                          >
                            {Number(row.ComparisonValue).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </TableCell>
                          {/* Variance Column */}
                          <TableCell
                            sx={{
                              color,
                              textAlign: "right",
                              width: 60,
                            }}
                          >
                            {Number(row.Variance).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </TableCell>
                          {/* Variance % Column */}
                          <TableCell
                            sx={{
                              textAlign: "right",
                              width: 70,
                              minWidth: 70,
                            }}
                          >
                            {variancePercent !== null && !isNaN(variancePercent)
                              ? `${Number(variancePercent).toFixed(0)}%`
                              : ""}
                          </TableCell>
                          {/* Risk Criteria Column */}
                          <TableCell
                            sx={{
                              textAlign: "center",
                              width: 150,
                            }}
                          >{`${row.RiskCriteria || ""}`}
                          </TableCell>
                          {/* comments Column */}
                          <TableCell
                            sx={{
                              textAlign: "center",
                              width: 180,
                              minWidth: 180,
                              float: "center",
                            }}
                          >
                            <TextareaAutosize
                              key={row.CommentsID}
                              disabled={commentsDisabled}
                              maxLength={4000}
                              onChange={(e) => {
                                handleCommentChange(
                                  row.CommentsID,
                                  row.CompanyID,
                                  e.target.value,
                                );
                              }}
                              value={row.Comments || ""}
                              style={{
                                width: "100%",
                                height: "32px",
                                minHeight: "28px",
                                fontSize: "0.70rem",
                                padding: "2px 2px",
                                lineHeight: "1",
                                border: "1px solid #999",
                                borderRadius: "4px",
                                resize: "vertical",
                                fontFamily: "inherit",
                                overflowY: "auto",
                                backgroundColor: commentsDisabled
                                  ? "#f0f0f0"
                                  : "transparent",
                                color: commentsDisabled ? "#888" : "inherit",
                                boxSizing: "border-box",
                              }}
                            />
                          </TableCell>
                          {/* Previous Period Comments Column */}
                          {showPreviousComments && (
                            <TableCell
                              sx={{
                                textAlign: "center",
                                width: 180,
                                minWidth: 180,
                                float: "center",
                              }}
                            >
                              <TextareaAutosize
                                disabled
                                readOnly
                                value={row.PreviousPeriodComments || ""}
                                style={{
                                  width: "100%",
                                  height: "32px",
                                  minHeight: "28px",
                                  fontSize: "0.70rem",
                                  padding: "2px 2px",
                                  lineHeight: "1",
                                  border: "1px solid #999",
                                  borderRadius: "4px",
                                  resize: "none",
                                  fontFamily: "inherit",
                                  overflowY: "auto",
                                  backgroundColor: "#f0f0f0",
                                  color: "#888",
                                  boxSizing: "border-box",
                                }}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={totalColumns} align="center">
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>{/* end overflowX:clip wrapper */}

          {/*
          * ── Floating horizontal scrollbar ─────────────────────────────────
          * position:fixed so it always floats at the bottom of the viewport
          * the moment the table enters view — no need to scroll to the last
          * record to find the scrollbar.
          * Visibility is driven by IntersectionObserver (floatingBarVisible).
          * Left + width are kept in sync with the table's bounding rect.
          */}
        </Paper>
        <Box
          ref={stickyScrollRef}
          sx={{
            position: "fixed",
            bottom: 0,
            left: floatingBarStyle.left ?? 0,
            width: floatingBarStyle.width ?? "100%",
            overflowX: "auto",
            overflowY: "hidden",
            height: 14,
            zIndex: 1300,
            bgcolor: "grey.100",
            borderTop: "2px solid",
            borderColor: "primary.main",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.15)",
            borderRadius: "4px 4px 0 0",
            opacity: floatingBarVisible ? 1 : 0,
            pointerEvents: floatingBarVisible ? "auto" : "none",
            transition: "opacity 0.2s ease",
            "&::-webkit-scrollbar": { height: 12 },
            "&::-webkit-scrollbar-track": {
              bgcolor: "grey.200",
              borderRadius: 1,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "primary.main",
              borderRadius: 1,
              "&:hover": { bgcolor: "primary.dark" },
            },
          }}
        >
          <div ref={innerWidthRef} style={{ height: 1 }} />
        </Box>
      </Box>{/* end outer wrapper */}
      {auditData && !loading && (
        <Grid container spacing={1} className="table-footer">
          <Grid size={{ xs: 6, md: 3, sm: 3 }}>
            <Grid component="section">
              <Typography variant="body2" sx={{ p: 1, textAlign: "center" }}>
                * Best viewed at 1080p resolution or higher.
              </Typography>
            </Grid>
          </Grid>

          <Grid size={{ xs: 6, md: 3.2, sm: 3.2 }}>
            <Grid component="section">
              <Typography variant="body2" sx={{ p: 1, textAlign: "right" }}>
                Grand Total:
              </Typography>
            </Grid>
          </Grid>
          <Grid size={{ xs: 6, md: 0.4, sm: 0.4 }}>
            <Grid component="section">
              <Typography
                variant="body2"
                sx={{ pt: 1, textAlign: "left", minWidth: 80 }}
              >
                {Number(totalAmountData?.TotalReportingValue).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  },
                )}{" "}
              </Typography>
            </Grid>
          </Grid>
          <Grid size={{ xs: 6, md: 0.34, sm: 0.34 }}>
            <Grid component="section">
              <Typography variant="body2" sx={{ pt: 1, textAlign: "left", minWidth: 80 }}>
                {Number(totalAmountData?.TotalComparisonValue).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  },
                )}{" "}
              </Typography>
            </Grid>
          </Grid>
          <Grid size={{ xs: 6, md: 1.55, sm: 1.55 }}>
            <Grid component="section">
              <Typography variant="body2" sx={{ p: 1, textAlign: "right" }}>
                Total Records: {auditData.TotalRecords}{" "}
              </Typography>
            </Grid>
          </Grid>
          <Grid size={{ xs: 6, md: 1.2, sm: 1.2 }}>
            <Grid component="section">
              <Typography variant="body2" sx={{ p: 1, textAlign: "right" }}>
                Showing: {from} - {to}{" "}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            size={{ xs: 12, md: 1.7, sm: 1.7 }}
            sx={{
              p: 0,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Grid
              component="section"
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TablePagination
                component="div"
                count={auditData.TotalRecords}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={100}
                rowsPerPageOptions={[]}
                labelRowsPerPage=""
                labelDisplayedRows={(opt) => {
                  return `Page ${auditData.TotalRecords ? page + 1 : 0} of ${auditData?.TotalPages ?? 0
                    }`;
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default PaginatedTable;